import {
    useCallback,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type PanResponderGestureState,
    type LayoutChangeEvent,
    PanResponder,
    Platform,
    View
} from "react-native";
import type IColorAreaProps from "./type";
import type {
    ColorAreaAccessibilityActionEventType,
    ColorAreaGestureHandlersType,
    ColorAreaKeyboardEventType,
    ColorAreaWebPropsType,
    ColorAreaChipSize,
    ColorAreaDragType,
    ColorAreaValue
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import ColorAreaPlane from "./components/plane";
import {
    COLOR_AREA_DEFAULT_GUIDES,
    COLOR_AREA_DEFAULT_VALUE,
    getColorAreaClampedValue,
    COLOR_AREA_ASPECT_RATIO,
    getColorAreaStripHeight,
    getColorAreaRingColor,
    getColorAreaStepValue,
    COLOR_AREA_NAME_KEYS,
    getColorAreaKeyValue,
    getColorAreaAxisMax,
    getColorAreaStrips,
    getColorAreaGuides,
    getColorAreaOrigin,
    getColorAreaPoint,
    getColorAreaThumb
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    getInteractionStateName,
    useInteractionState
} from "../../helpers/interaction";
import {
    getCuspChroma,
    getColorName,
    oklchToHex,
    parseColor
} from "../../helpers/color";
import {
    formatPercent,
    formatNumber
} from "../../helpers/locale";
import {
    measurePointerRect
} from "../../helpers/pointer";
import Text from "../text";

const ColorArea: FC<IColorAreaProps> = ({
    contrastGuides = COLOR_AREA_DEFAULT_GUIDES,
    initialValue = COLOR_AREA_DEFAULT_VALUE,
    aspectRatio = COLOR_AREA_ASPECT_RATIO,
    "aria-label": ariaLabelProp,
    isGamutShown = true,
    isDisabled = false,
    shape = "square",
    value: valueProp,
    contrastSurface,
    isFlush = false,
    customLocalize,
    customTheme,
    onChangeEnd,
    alpha = 1,
    onChange,
    radius,
    style,
    title,
    hue,
    ...props
}) => {
    const {
        shapeConfigs,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        innerValue,
        setInnerValue
    ] = useState<ColorAreaValue>(initialValue);

    const [
        width,
        setWidth
    ] = useState<number>(0);

    const [
        isDragging,
        setIsDragging
    ] = useState<boolean>(false);

    const [
        chipSizes,
        setChipSizes
    ] = useState<Record<number, ColorAreaChipSize>>({});

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDisabled
    });

    const areaRef = useRef<View>(null);

    const dragRef = useRef<ColorAreaDragType>({
        isChanged: false,
        isActive: false,
        origin: null
    });

    const keyChangedRef = useRef<boolean>(false);

    const height = width / aspectRatio;

    const axisMax = useMemo(() => getColorAreaAxisMax(getCuspChroma(hue)), [
        hue
    ]);

    const value = useMemo(() => getColorAreaClampedValue({
        ...(valueProp ?? innerValue),
        hue
    }), [
        valueProp,
        innerValue,
        hue
    ]);

    const valueRef = useRef<ColorAreaValue>(value);

    valueRef.current = value;

    const surfaceColor = contrastSurface ?? colors.content.container.default;

    const guidesKey = contrastGuides.join(",");

    const strips = useMemo(() => {
        if(width <= 0 || height <= 0) {
            return null;
        }

        return getColorAreaStrips({
            stripHeight: getColorAreaStripHeight(),
            axisMax,
            height,
            shape,
            width,
            hue
        });
    }, [
        axisMax,
        height,
        shape,
        width,
        hue
    ]);

    const guides = useMemo(() => {
        const surface = parseColor(surfaceColor)?.rgb;

        if(!guidesKey || !surface || width <= 0 || height <= 0) {
            return [];
        }

        return getColorAreaGuides({
            contrastGuides: guidesKey.split(",").map(Number),
            surface,
            axisMax,
            height,
            shape,
            width,
            alpha,
            hue
        });
    }, [
        surfaceColor,
        guidesKey,
        axisMax,
        height,
        shape,
        width,
        alpha,
        hue
    ]);

    const thumbColor = oklchToHex(value.lightness, value.chroma, hue);

    const ringColor = getColorAreaRingColor({
        colors
    });

    const {
        container: containerDynamicStyle,
        overlay: overlayDynamicStyle,
        frame: frameDynamicStyle,
        thumb: thumbDynamicStyle,
        clip: clipDynamicStyle
    } = useStyles({
        thumb: getColorAreaThumb({
            axisMax,
            height,
            shape,
            width,
            value,
            hue
        }),
        isFocusVisible,
        shapeConfigs,
        isDragging,
        thumbColor,
        isDisabled,
        ringColor,
        iconSizes,
        radiuses,
        isFlush,
        borders,
        colors,
        height,
        radius
    });

    const handleCommit = useCallback((next: ColorAreaValue) => {
        const current = valueRef.current;

        if(next.lightness === current.lightness && next.chroma === current.chroma) {
            return false;
        }

        valueRef.current = next;

        if(valueProp === undefined) {
            setInnerValue(next);
        }

        onChange?.(next);

        return true;
    }, [
        valueProp,
        onChange
    ]);

    const handlePointer = (pointerX: number, pointerY: number) => {
        const origin = dragRef.current.origin;

        if(!origin || width <= 0 || height <= 0) {
            return;
        }

        const next = getColorAreaPoint({
            direction,
            pointerX,
            pointerY,
            axisMax,
            origin,
            height,
            shape,
            width,
            hue
        });

        if(handleCommit(next)) {
            dragRef.current.isChanged = true;
        }
    };

    const handleGrant = ({
        x0,
        y0
    }: PanResponderGestureState) => {
        if(isDisabled) {
            return;
        }

        dragRef.current = {
            isChanged: false,
            isActive: true,
            origin: null
        };

        setIsDragging(true);

        measurePointerRect(areaRef.current, ({
            width: measuredWidth,
            pageX,
            pageY
        }) => {
            dragRef.current.origin = getColorAreaOrigin({
                width: measuredWidth,
                direction,
                pageX,
                pageY
            });

            handlePointer(x0, y0);

            if(!dragRef.current.isActive && dragRef.current.isChanged) {
                onChangeEnd?.(valueRef.current);
            }
        });
    };

    const handleMove = ({
        moveX,
        moveY
    }: PanResponderGestureState) => {
        if(!dragRef.current.isActive) {
            return;
        }

        handlePointer(moveX, moveY);
    };

    const handleRelease = () => {
        const drag = dragRef.current;

        drag.isActive = false;

        setIsDragging(false);

        if(drag.isChanged) {
            drag.isChanged = false;

            onChangeEnd?.(valueRef.current);
        }
    };

    const latestRef = useRef<ColorAreaGestureHandlersType>({
        handleRelease,
        handleGrant,
        isDisabled,
        handleMove
    });

    latestRef.current = {
        handleRelease,
        handleGrant,
        isDisabled,
        handleMove
    };

    const panResponder = useMemo(() => PanResponder.create({
        onPanResponderGrant: (_event, gestureState) => latestRef.current.handleGrant(gestureState),
        onPanResponderMove: (_event, gestureState) => latestRef.current.handleMove(gestureState),
        onStartShouldSetPanResponder: () => !latestRef.current.isDisabled,
        onMoveShouldSetPanResponder: () => !latestRef.current.isDisabled,
        onPanResponderTerminate: () => latestRef.current.handleRelease(),
        onPanResponderRelease: () => latestRef.current.handleRelease(),
        onPanResponderTerminationRequest: () => false
    }), []);

    const onLayout = ({
        nativeEvent
    }: LayoutChangeEvent) => {
        setWidth(nativeEvent.layout.width);
    };

    const onKeyDown = (event: ColorAreaKeyboardEventType) => {
        if(isDisabled || event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        const next = getColorAreaKeyValue({
            shiftKey: event.shiftKey,
            value: valueRef.current,
            key: event.key,
            direction,
            shape,
            hue
        });

        if(!next) {
            return;
        }

        event.preventDefault();

        if(handleCommit(next)) {
            keyChangedRef.current = true;
        }
    };

    const onKeyUp = () => {
        if(!keyChangedRef.current) {
            return;
        }

        keyChangedRef.current = false;

        onChangeEnd?.(valueRef.current);
    };

    const onAccessibilityAction = ({
        nativeEvent
    }: ColorAreaAccessibilityActionEventType) => {
        if(nativeEvent.actionName !== "increment" && nativeEvent.actionName !== "decrement") {
            return;
        }

        const next = getColorAreaStepValue({
            direction: nativeEvent.actionName === "increment" ? 1 : -1,
            value: valueRef.current,
            shape,
            hue
        });

        if(handleCommit(next)) {
            onChangeEnd?.(next);
        }
    };

    const getColorLabel = () => {
        const name = getColorName({
            l: value.lightness,
            c: value.chroma,
            h: hue
        });

        const colorName = localize(COLOR_AREA_NAME_KEYS[name.key as keyof typeof COLOR_AREA_NAME_KEYS] ?? "colorName-gray");

        if(!name.modifier) {
            return colorName;
        }

        return localize(name.modifier, [
            colorName
        ]);
    };

    const getValueText = () => {
        return localize("colorArea-valueText", [
            formatPercent({
                options: {
                    maximumFractionDigits: 0
                },
                value: value.lightness,
                locale: activeLocale
            }),
            formatNumber({
                options: {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3
                },
                locale: activeLocale,
                value: value.chroma
            }),
            getColorLabel()
        ]);
    };

    const handleChipLayout = (index: number, event: LayoutChangeEvent) => {
        const {
            height: chipHeight,
            width: chipWidth
        } = event.nativeEvent.layout;

        setChipSizes((current) => {
            const known = current[index];

            if(known && known.width === chipWidth && known.height === chipHeight) {
                return current;
            }

            return {
                ...current,
                [index]: {
                    height: chipHeight,
                    width: chipWidth
                }
            };
        });
    };

    const webProps: ColorAreaWebPropsType = Platform.OS === "web" ? {
        dataSet: {
            [INTERACTION_STATE_DATA_KEY]: getInteractionStateName({
                isPressed: isDragging,
                isHovered: false,
                isFocusVisible,
                isDisabled
            })
        },
        onKeyDown,
        onKeyUp
    } : {};

    const renderChips = () => {
        return guides.map((guide, index) => {
            if(!guide.top) {
                return null;
            }

            const size = chipSizes[index];

            const chipWidth = size?.width ?? 0;

            const chipHeight = size?.height ?? 0;

            const top = Math.min(Math.max(guide.top[1] - chipHeight - spaces.spacingXs, spaces.spacingXs), Math.max(spaces.spacingXs, height - chipHeight - spaces.spacingXs));

            const start = Math.min(Math.max(guide.top[0] - chipWidth / 2, spaces.spacingXs), Math.max(spaces.spacingXs, width - chipWidth - spaces.spacingXs));

            return <View
                style={[
                    stylesheet.chip,
                    {
                        backgroundColor: colors.content.container.default,
                        paddingHorizontal: spaces.spacingXs,
                        borderRadius: radiuses.chip,
                        opacity: size ? 1 : 0,
                        start,
                        top
                    }
                ]}
                onLayout={(event) => handleChipLayout(index, event)}
                key={`chip-${guide.target}`}
                aria-hidden={true}
            >
                <Text
                    customColor={colors.content.text.high}
                    customTheme={customTheme}
                    variant="labelSmallSize"
                    numberOfLines={1}
                >
                    {`${formatNumber({
                        options: {
                            maximumFractionDigits: 2
                        },
                        locale: activeLocale,
                        value: guide.target
                    })}:1`}
                </Text>
            </View>;
        });
    };

    return <View
        {...props}
        {...webProps}
        {...panResponder.panHandlers}
        aria-label={ariaLabelProp ?? title ?? localize("colorArea-label")}
        aria-roledescription={localize("colorArea-roleDescription")}
        aria-valuenow={Math.round(value.lightness * 100)}
        onAccessibilityAction={onAccessibilityAction}
        style={[
            style,
            stylesheet.container,
            isDragging ? stylesheet.dragging : null,
            isDisabled ? stylesheet.disabled : null,
            containerDynamicStyle
        ]}
        onFocus={interactionHandlers.onFocus}
        onBlur={interactionHandlers.onBlur}
        aria-valuetext={getValueText()}
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        accessibilityActions={[
            {
                name: "increment"
            },
            {
                name: "decrement"
            }
        ]}
        focusable={!isDisabled}
        onLayout={onLayout}
        aria-valuemax={100}
        accessible={true}
        aria-valuemin={0}
        role="slider"
        ref={areaRef}
    >
        <View
            style={[
                stylesheet.layer,
                stylesheet.clip,
                clipDynamicStyle
            ]}
        >
            <ColorAreaPlane
                isGamutShown={isGamutShown && shape === "gamut"}
                customTheme={customTheme}
                direction={direction}
                strips={strips}
                guides={guides}
                height={height}
                width={width}
            />
            {renderChips()}
            {isDisabled && <View
                style={[
                    stylesheet.layer,
                    overlayDynamicStyle
                ]}
            />}
        </View>
        <View
            style={[
                stylesheet.frame,
                frameDynamicStyle
            ]}
        />
        {width > 0 && <View
            style={[
                stylesheet.thumb,
                thumbDynamicStyle
            ]}
        />}
    </View>;
};
export default ColorArea;
